import { decamelize } from 'humps';
import { callAPI, callAPIWithTeam } from 'utils';
import API from 'constants/api';

import { CurrentView } from 'types/common';
import { OrganisationAT } from 'redux/actionTypes/common';

import {
  MemberInvitationSchema,
  PermissionSchema,
  RoleSchema,
  TeamMembershipSchema,
  OrganisationsSchema,
  OrganisationSummarySchema,
} from 'redux/schemas';

import {
  FetchInvitationAction,
  DeleteInvitationAction,
  FetchInvitationListAction,
  FetchMembershipListAction,
  FetchOrganisationProfileAction,
  FetchTeamMembershipAction,
  MyTeamsAction,
  Organisation,
  PatchTeamMembershipAction,
  PermissionAction,
  RoleAction,
  SendInvitationAction,
  SetCurrentTeam,
  TeamMember,
  MemberInvitationFormData,
  SetCurrentView,
  AcceptInvitationAction,
  CreateOrganisationAction,
  ConnectStripeAction,
  FetchOrgSubscriptionsAction,
  FetchMembershipLicenseAction,
  RedeemMembershipLicenseAction,
} from 'types/common';

export const listRoles = () => {
  return callAPI<RoleAction>({
    types: [
      OrganisationAT.FETCH_ROLE_REQUEST,
      OrganisationAT.FETCH_ROLE_SUCCESS,
      OrganisationAT.FETCH_ROLE_FAILURE,
    ],
    endpoint: API.organisation.roles,
    method: 'GET',
    schema: [RoleSchema],
    path: 'results',
  });
};

export const listPermissions = () => {
  return callAPI<PermissionAction>({
    types: [
      OrganisationAT.FETCH_PERMISSION_REQUEST,
      OrganisationAT.FETCH_PERMISSION_SUCCESS,
      OrganisationAT.FETCH_PERMISSION_FAILURE,
    ],
    endpoint: API.organisation.permissions,
    method: 'GET',
    schema: [PermissionSchema],
    path: 'results',
  });
};

export const fetchMyTeams = () => {
  return callAPI<MyTeamsAction>({
    types: [
      OrganisationAT.FETCH_MY_TEAMS_REQUEST,
      OrganisationAT.FETCH_MY_TEAMS_SUCCESS,
      OrganisationAT.FETCH_MY_TEAMS_FAILURE,
    ],
    endpoint: API.organisation.myTeams,
    method: 'GET',
    path: 'results',
  });
};

export const listTeamMembers = () => {
  return callAPIWithTeam<FetchMembershipListAction>({
    types: [
      OrganisationAT.FETCH_TEAM_MEMBER_LIST_REQUEST,
      OrganisationAT.FETCH_TEAM_MEMBER_LIST_SUCCESS,
      OrganisationAT.FETCH_TEAM_MEMBER_LIST_FAILURE,
    ],
    endpoint: API.organisation.teamMembers(),
    method: 'GET',
    schema: [TeamMembershipSchema],
    path: 'results',
    meta: {
      toast: {
        error: 'An error occurred loading your your Team Members.',
      },
    },
  });
};

export const fetchTeamMember = (teamMembershipId: number) => {
  return callAPIWithTeam<FetchTeamMembershipAction>({
    types: [
      OrganisationAT.FETCH_TEAM_MEMBER_REQUEST,
      OrganisationAT.FETCH_TEAM_MEMBER_SUCCESS,
      OrganisationAT.FETCH_TEAM_MEMBER_FAILURE,
    ],
    endpoint: API.organisation.teamMembers(teamMembershipId),
    method: 'GET',
    schema: TeamMembershipSchema,
  });
};

export const updateTeamMember = (
  teamMembershipId: number,
  data: Partial<TeamMember>
) => {
  return callAPIWithTeam<PatchTeamMembershipAction>({
    types: [
      OrganisationAT.PATCH_TEAM_MEMBER_REQUEST,
      OrganisationAT.PATCH_TEAM_MEMBER_SUCCESS,
      OrganisationAT.PATCH_TEAM_MEMBER_FAILURE,
    ],
    endpoint: API.organisation.teamMembers(teamMembershipId),
    method: 'PATCH',
    schema: TeamMembershipSchema,
    body: data,
    meta: {
      toast: { success: 'Successfully updated permissions.' },
    },
  });
};

export const deleteTeamMember = (teamId: number, teamMembershipId: number) => {
  return callAPIWithTeam<PatchTeamMembershipAction>({
    types: [
      OrganisationAT.DELETE_TEAM_MEMBER_REQUEST,
      OrganisationAT.DELETE_TEAM_MEMBER_SUCCESS,
      OrganisationAT.DELETE_TEAM_MEMBER_FAILURE,
    ],
    endpoint: API.organisation.teamMembers(teamMembershipId),
    meta: {
      teamId,
      memberId: teamMembershipId,
      toast: { success: 'Successfully removed team member.' },
    },
    method: 'DELETE',
  });
};

export const fetchOrganisationSummary = (handle: string) => {
  return callAPI<FetchOrganisationProfileAction>({
    types: [
      OrganisationAT.FETCH_ORGANISATION_REQUEST,
      OrganisationAT.FETCH_ORGANISATION_SUCCESS,
      OrganisationAT.FETCH_ORGANISATION_FAILURE,
    ],
    endpoint: API.organisation.organisationSummary(handle),
    schema: OrganisationSummarySchema,
    method: 'GET',
  });
};

export const fetchOrganisationProfile = (team: number) => {
  return callAPI<FetchOrganisationProfileAction>({
    types: [
      OrganisationAT.FETCH_ORGANISATION_REQUEST,
      OrganisationAT.FETCH_ORGANISATION_SUCCESS,
      OrganisationAT.FETCH_ORGANISATION_FAILURE,
    ],
    endpoint: API.organisation.organisationProfile(team),
    schema: OrganisationsSchema,
    method: 'GET',
  });
};

export const updateOrganisationProfile = (
  team: number,
  data: Partial<Organisation>
) => {
  const formData = new FormData();

  Object.keys(data).forEach((k) => {
    if (k === 'logo' && ['string', 'undefined'].includes(typeof data[k])) {
      return;
    }

    const key = k as never;
    formData.append(decamelize(k), data[key]);
  });

  return callAPI<FetchOrganisationProfileAction>({
    types: [
      OrganisationAT.UPDATE_ORGANISATION_REQUEST,
      OrganisationAT.UPDATE_ORGANISATION_SUCCESS,
      OrganisationAT.UPDATE_ORGANISATION_FAILURE,
    ],
    endpoint: API.organisation.organisationProfile(team),
    schema: OrganisationsSchema,
    body: formData,
    method: 'PATCH',
  });
};

export const fetchOrganisationTraining = (team: number) => {
  return callAPI<FetchOrganisationProfileAction>({
    types: [
      OrganisationAT.FETCH_ORGANISATION_REQUEST,
      OrganisationAT.FETCH_ORGANISATION_SUCCESS,
      OrganisationAT.FETCH_ORGANISATION_FAILURE,
    ],
    endpoint: API.organisation.organisationTraining(team),
    schema: OrganisationsSchema,
    method: 'GET',
  });
};

export const updateOrganisationTraining = (
  team: number,
  data: Partial<Organisation>
) => {
  const formData = new FormData();

  Object.keys(data).forEach((k) => {
    if (k === 'logo' && ['string', 'undefined'].includes(typeof data[k])) {
      return;
    }

    const key = k as never;
    formData.append(decamelize(k), data[key]);
  });

  return callAPI<FetchOrganisationProfileAction>({
    types: [
      OrganisationAT.UPDATE_ORGANISATION_REQUEST,
      OrganisationAT.UPDATE_ORGANISATION_SUCCESS,
      OrganisationAT.UPDATE_ORGANISATION_FAILURE,
    ],
    endpoint: API.organisation.organisationTraining(team),
    schema: OrganisationsSchema,
    body: formData,
    method: 'PATCH',
    meta: { toast: false },
  });
};

export const sendMemberInvitation = (data: MemberInvitationFormData) => {
  return callAPIWithTeam<SendInvitationAction>({
    types: [
      OrganisationAT.SEND_INVITATION_REQUEST,
      OrganisationAT.SEND_INVITATION_SUCCESS,
      OrganisationAT.SEND_INVITATION_FAILURE,
    ],
    endpoint: API.organisation.teamMemberInvitation(),
    body: data,
    method: 'POST',
    meta: { toast: { error: true, success: 'Invitation sent' } },
  });
};

export const resendMemberInvitation = (id: number) => {
  return callAPIWithTeam<SendInvitationAction>({
    types: [
      OrganisationAT.RESEND_INVITATION_REQUEST,
      OrganisationAT.RESEND_INVITATION_SUCCESS,
      OrganisationAT.RESEND_INVITATION_FAILURE,
    ],
    endpoint: API.organisation.resendInvitation(id),
    method: 'POST',
    meta: {
      toast: { error: true, success: 'Invitation successfully resent' },
    },
  });
};

export const listMemberInvitation = () => {
  return callAPIWithTeam<FetchInvitationListAction>({
    types: [
      OrganisationAT.FETCH_INVITATION_LIST_REQUEST,
      OrganisationAT.FETCH_INVITATION_LIST_SUCCESS,
      OrganisationAT.FETCH_INVITATION_LIST_FAILURE,
    ],
    endpoint: API.organisation.teamMemberInvitation(),
    method: 'GET',
  });
};

export const retrieveMemberInvitation = (invitationKey: string) => {
  return callAPI<FetchInvitationAction>({
    types: [
      OrganisationAT.FETCH_INVITATION_REQUEST,
      OrganisationAT.FETCH_INVITATION_SUCCESS,
      OrganisationAT.FETCH_INVITATION_FAILURE,
    ],
    endpoint: API.organisation.invitation(invitationKey),
    method: 'GET',
    schema: MemberInvitationSchema,
    meta: { toast: false },
  });
};

export const deleteMemberInvitation = (id: number) => {
  return callAPIWithTeam<DeleteInvitationAction>({
    types: [
      OrganisationAT.DELETE_INVITATION_REQUEST,
      OrganisationAT.DELETE_INVITATION_SUCCESS,
      OrganisationAT.DELETE_INVITATION_FAILURE,
    ],
    endpoint: API.organisation.deleteInvitation(id),
    method: 'DELETE',
    meta: {
      inviteId: id,
      toast: { success: 'Invitation revoked' },
    },
  });
};

export const acceptMemberInvitation = (invitationKey: string) => {
  return callAPI<AcceptInvitationAction>({
    types: [
      OrganisationAT.ACCEPT_INVITATION_REQUEST,
      OrganisationAT.ACCEPT_INVITATION_SUCCESS,
      OrganisationAT.ACCEPT_INVITATION_FAILURE,
    ],
    endpoint: API.organisation.invitation(invitationKey),
    method: 'POST',
    schema: MemberInvitationSchema,
    meta: { toast: { success: false } },
  });
};

export const setCurrentTeam = (team: number | null): SetCurrentTeam => ({
  type: OrganisationAT.SET_CURRENT_TEAM,
  payload: team,
  error: false,
});

export const setCurrentView = (view: CurrentView): SetCurrentView => ({
  type: OrganisationAT.SET_CURRENT_VIEW,
  payload: view,
  error: false,
});

export const createOrganisation = (data: Partial<Organisation>) => {
  const formData = new FormData();
  Object.keys(data).forEach((k) => {
    const key = k as never;
    formData.append(decamelize(k), data[key]);
  });
  return callAPI<CreateOrganisationAction>({
    types: [
      OrganisationAT.CREATE_ORGANISATION_REQUEST,
      OrganisationAT.CREATE_ORGANISATION_SUCCESS,
      OrganisationAT.CREATE_ORGANISATION_FAILURE,
    ],
    endpoint: API.organisation.create,
    method: 'POST',
    body: formData,
  });
};

export const connectStripe = ({ businessType }: { businessType?: string }) => {
  const formData = new FormData();
  if (businessType) {
    formData.append('business_type', `${businessType}`);
  }
  return callAPIWithTeam<ConnectStripeAction>({
    types: [
      OrganisationAT.CONNECT_STRIPE_REQUEST,
      OrganisationAT.CONNECT_STRIPE_SUCCESS,
      OrganisationAT.CONNECT_STRIPE_FAILURE,
    ],
    endpoint: API.cms.connectStripe,
    method: 'POST',
    meta: {
      toast: {
        success: false,
        error: false,
      },
    },
    body: formData,
  });
};

export const fetchSubscriptions = (redirect?: boolean) => {
  return callAPIWithTeam<FetchOrgSubscriptionsAction>({
    types: [
      OrganisationAT.FETCH_ORG_SUBSCRIPTIONS_REQUEST,
      OrganisationAT.FETCH_ORG_SUBSCRIPTIONS_SUCCESS,
      OrganisationAT.FETCH_ORG_SUBSCRIPTIONS_FAILURE,
    ],
    endpoint: `${API.organisation.subscriptions}${
      redirect ? '?redirect=true' : ''
    }`,
    method: 'GET',
  });
};

export const retrieveMembershipLicense = (slug: string) => {
  return callAPI<FetchMembershipLicenseAction>({
    types: [
      OrganisationAT.FETCH_MEMBERSHIP_LICENSE_REQUEST,
      OrganisationAT.FETCH_MEMBERSHIP_LICENSE_SUCCESS,
      OrganisationAT.FETCH_MEMBERSHIP_LICENSE_FAILURE,
    ],
    endpoint: API.organisation.retrieveMembershipLicense(slug),
    method: 'GET',
    meta: { toast: false },
  });
};

export const redeemMembershipLicense = ({
  slug,
  teamId,
}: {
  slug: string;
  teamId: number;
}) => {
  const formData = new FormData();
  formData.append('team_id', `${teamId}`);
  return callAPI<RedeemMembershipLicenseAction>({
    types: [
      OrganisationAT.REDEEM_MEMBERSHIP_LICENSE_REQUEST,
      OrganisationAT.REDEEM_MEMBERSHIP_LICENSE_SUCCESS,
      OrganisationAT.REDEEM_MEMBERSHIP_LICENSE_FAILURE,
    ],
    endpoint: API.organisation.redeemMembershipLicense(slug),
    method: 'POST',
    meta: { toast: false },
    body: formData,
  });
};
